import { Badge, Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterOutlinedIcon, GartheringIcon, GeofenceIcon, OfflineIcon, RemoveDeviceIcon } from '~/assets/icons';
import { IRecentAlertList } from '~/utils/interface/dashboard';
import { handleApiError, tzDayjs } from '~/utils/helper';
import { AlertStatusEnum, AlertTypeEnum } from '~/utils/enum';
import { BaseScrollbar } from '~/components/common';
import { useAppSelector } from '~/redux/hooks';
import dashboardApi from '~/apis/dashboard';

const RecentAlert = () => {
  const { t } = useTranslation('admin/home');

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const currentUser = Object.values(selectedUser).length === 0 ? userProfile : selectedUser;

  const [recentAlertData, setRecentAlertData] = useState<Array<IRecentAlertList>>([]);
  const [anchorFilter, setAnchorFilter] = useState<null | HTMLElement>(null);
  const isOpenFilter = Boolean(anchorFilter);
  const [selectedFilter, setSelectedFilter] = useState<number>(-1);

  const filterOptions = useMemo(() => {
    return [
      {
        id: -1,
        name: t('all'),
      },
      {
        id: AlertTypeEnum.REMOVE_DEVICE,
        name: t('alertType.' + AlertTypeEnum.REMOVE_DEVICE),
      },
      {
        id: AlertTypeEnum.GEOFENCE,
        name: t('alertType.' + AlertTypeEnum.GEOFENCE),
      },
      {
        id: AlertTypeEnum.GATHERING,
        name: t('alertType.' + AlertTypeEnum.GATHERING),
      },
      {
        id: AlertTypeEnum.OFFLINE,
        name: t('alertType.' + AlertTypeEnum.OFFLINE),
      },
    ];
  }, [t]);

  useEffect(() => {
    if (!currentUser?.username) return;

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const response = await dashboardApi.getRecentAlert({ username: currentUser.username });
        if (!isCancelled) {
          setRecentAlertData(response?.data?.result);
        }
      } catch (error) {
        if (!isCancelled) handleApiError(error);
      } finally {
        if (!isCancelled) {
          timeoutId = setTimeout(poll, 10000); // 10s
        }
      }
    };

    poll();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [currentUser?.username]);

  const renderIcon = (type: number) => {
    if (type === AlertTypeEnum.REMOVE_DEVICE) return <RemoveDeviceIcon className='w-5 h-5 cursor-pointer' />;
    if (type === AlertTypeEnum.GEOFENCE) return <GeofenceIcon className='w-5 h-5 cursor-pointer' />;
    if (type === AlertTypeEnum.GATHERING) return <GartheringIcon className='w-5 h-5 cursor-pointer' />;
    if (type === AlertTypeEnum.OFFLINE) return <OfflineIcon className='w-5 h-5 cursor-pointer' />;
  };

  const renderName = (data: any) => {
    const type: number = data.id;

    if (type === AlertTypeEnum.REMOVE_DEVICE)
      return (
        <Typography className={`px-3 text-base text-[#7C3AED] ${data.id === selectedFilter ? 'font-bold' : ''} `}>
          {data.name}
        </Typography>
      );
    else if (type === AlertTypeEnum.GEOFENCE)
      return (
        <Typography className={`px-3 text-base text-[#EF4444] ${data.id === selectedFilter ? 'font-bold' : ''}`}>
          {data.name}
        </Typography>
      );
    else if (type === AlertTypeEnum.GATHERING)
      return (
        <Typography className={`px-3 text-base text-[#C54D09] ${data.id === selectedFilter ? 'font-bold' : ''}`}>
          {data.name}
        </Typography>
      );
    else if (type === AlertTypeEnum.OFFLINE)
      return (
        <Typography className={`px-3 text-base text-[#F97316] ${data.id === selectedFilter ? 'font-bold' : ''}`}>
          {data.name}
        </Typography>
      );
    else {
      return (
        <Typography className={`px-3 text-base text-[#123B6D] ${data.id === selectedFilter ? 'font-bold' : ''}`}>
          {data.name}
        </Typography>
      );
    }
  };

  const filteredData =
    recentAlertData?.filter((item) => {
      if (selectedFilter === -1) return true;
      return selectedFilter === item.type;
    }) || [];

  return (
    <Box className='rounded-xl bg-white flex flex-col h-[763px]'>
      <Box className='flex rounded-t-xl justify-between items-center w-full bg-[#C62828]'>
        <Typography className='p-3 text-white font-semibold text-xl rounded-t-xl w-full'>
          {t('recentAlert.title')}
        </Typography>
        <IconButton
          className='mr-2'
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setAnchorFilter(event.currentTarget);
          }}
        >
          <Badge
            badgeContent={filteredData.length}
            color='primary'
            max={999}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                backgroundColor: '#2e89c8',
              },
            }}
          >
            <FilterOutlinedIcon stroke='#ffffff' className='h-6 w-6' />
          </Badge>
        </IconButton>
      </Box>
      <Menu
        open={isOpenFilter}
        anchorEl={anchorFilter}
        onClose={() => setAnchorFilter(null)}
        className='[&>.MuiPaper-root]:!p-2 [&>.MuiPaper-root]:min-w-[204px]'
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {filterOptions.map((option, index) => {
          return (
            <MenuItem
              key={index}
              onClick={() => {
                setSelectedFilter(option.id);
                setAnchorFilter(null);
              }}
              className={`${option.id === selectedFilter ? 'bg-[#F3F6F9]' : ''}`}
            >
              {renderName(option)}
            </MenuItem>
          );
        })}
      </Menu>
      <Box className='p-2'>
        <BaseScrollbar maxHeight='692px'>
          {filteredData.length === 0 ? (
            <Typography className='text-sm text-center text-[#404854]'>
              {t('noData', { ns: 'admin/common' })}
            </Typography>
          ) : (
            filteredData.map((item) => {
              return (
                <Box className='flex items-center border rounded-xl px-4 py-3 mb-3 gap-4 w-[97%]' key={item.id}>
                  <Box className='w-7 h-7 flex items-center justify-center bg-[#FEE1E1] rounded-[6px]'>
                    {renderIcon(item.type)}
                  </Box>
                  <Box className='flex flex-col justify-center w-48'>
                    <Typography className='font-bold text-sm text-[#404854]'>
                      {t('recentAlert.type.' + item.type)}
                    </Typography>
                    <Typography className='text-sm text-[#404854]'>
                      {item.time ? tzDayjs(item.time).format('HH:mm DD/M/YYYY') : ''}
                    </Typography>
                  </Box>
                  <Box className='flex flex-col justify-center w-40'>
                    <Typography className='text-sm font-medium text-[#404854]'>{item.person_name}</Typography>
                    <Typography className='text-sm font-medium text-[#404854]'>{item.imei}</Typography>
                  </Box>
                  <Box className='ml-auto'>
                    <Typography
                      className={`min-w-20 text-center rounded-full px-4 py-2 font-medium ${
                        item.status === AlertStatusEnum.UNPROCESSED
                          ? 'bg-[#C628281A] text-[#C62828]'
                          : 'bg-[#14CB4B1A] text-[#17A62B]'
                      }`}
                    >
                      {t('recentAlert.status.' + item.status)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </BaseScrollbar>
      </Box>
    </Box>
  );
};

export default memo(RecentAlert);
